/* eslint-disable import/no-cycle */
import axios from 'axios';
import {
  CLEAR_CART_API,
  UPDATE_CART_API,
} from '../app/containers/HomePage/constants';
import {
  ADD_TO_CART_API,
  REMOVE_FROM_CART_API,
} from '../app/containers/Init/constants';
import { goToCart } from './navigation';
import { getShop, isInaEss } from './pageData';
import {
  getNumberOfTimesVariantWasAutoAdded,
  getMrDiscountCode,
  setNumberOfTimesVariantWasAutoAdded,
  getIsShopifyFunctionsEnabled,
  getAutoAddedVariantsDataWithRepetetionCount,
  setAutoAddedVariantsDataWithRepitionCount,
  getCVBAutoAddedVariantsLimits,
  setCVBAutoAddedVariantsLimits,
} from './settings';
import { matchStringsWithoutCaseSensitivity } from './strings';
import { logger } from './logger';
import { fgAddEvent } from '../freegift';
import { StaticMutexKeys, mutexManager } from './mutexmanager';
import {
  addShopifyFunctionsAttributeToCartUtils,
  debounceUtils,
  fetchCartUtils,
  getMRCartFromShopifyCartUtils,
  refreshAJAXCartUtils,
  removeShipProAndModifyCartIfNotEnabledUtils,
  triggerRerenderCartUtils,
  updateCartUtils,
} from '../index-utils';
import { logRuntimeError, logRuntimeInfo } from './utils';

export async function fetchCart() {
  const cart = await fetchCartUtils();
  return cart;
}

export const updateCart = async ({ updates, isSkipIntercept = false }) => {
  const result = await updateCartUtils({
    updates,
    isSkipIntercept,
  });
  return result;
};

export async function changeCart(variantid, quantity, selling_plan = null) {
  try {
    const formData = { id: variantid.toString(), quantity };
    if (selling_plan !== null) {
      formData.selling_plan = selling_plan;
    }
    const httpArgs = [REMOVE_FROM_CART_API, formData];
    const response = await axios.post(...httpArgs);
    logger.log('cart changed', formData, response);
    return response.data;
  } catch (e) {
    logger.log('failed to remove variant');
  }
}

export async function clearCart() {
  const httpArgs = [CLEAR_CART_API];
  await axios.post(...httpArgs);
}

export async function cartCleanup() {
  const cart = await fetchCart();
  const cartMap = cart.items.reduce((acc, item) => {
    acc[item.key] = 0;
    return acc;
  }, {});
  await updateCart({
    updates: cartMap,
    isSkipIntercept: true,
  });
}

export async function changeCartVartiantWithoutProperties(variants, cart) {
  let currCart = cart;

  const removedFromStateItems = [];

  for (let i = 0; i < variants.length; i++) {
    if (variants[i].quantity > 0) {
      if (!currCart) {
        currCart = await fetchCart();
      }
      let quantityToRemove = variants[i].quantity;

      while (quantityToRemove > 0) {
        const lineItem = getVariantLineFromCartWithoutProperties(
          variants[i].sp_variant_id,
          currCart,
        );

        const data = {};
        data.line = lineItem.line;
        if (quantityToRemove > lineItem.quantity) {
          quantityToRemove -= lineItem.quantity;
          data.quantity = 0;
        } else if (quantityToRemove < lineItem.quantity) {
          const newQuantity = lineItem.quantity - quantityToRemove;
          quantityToRemove = 0;
          data.quantity = newQuantity;
        } else {
          quantityToRemove = 0;
          data.quantity = 0;
        }
        const httpArgs = [REMOVE_FROM_CART_API, data];
        const resp = await axios.post(...httpArgs);
        const { data: respData } = resp;
        currCart = respData;
      }

      removedFromStateItems.push({
        variant_id: variants[i].sp_variant_id,
        old_quantity: variants[i].quantity,
        product_id: variants[i].sp_product_id,
        variant_title: variants[i].variant_title,
      });
    }
  }

  return removedFromStateItems;
}

export async function addOrderIDAttributeToCart(orderid) {
  if (getShop() === 'purcosmetics-shop.myshopify.com') {
    return;
  }

  if (
    getIsShopifyFunctionsEnabled() &&
    getShop() !== 'monk-commerce.myshopify.com'
  ) {
    return;
  }

  const formData = { attributes: { _mrOrderID: orderid } };
  const httpArgs = [`${UPDATE_CART_API}&mrs=1`, formData];
  await axios.post(...httpArgs);
}

export async function addShopifyFunctionsAttributeToCart(orderid) {
  const result = await addShopifyFunctionsAttributeToCartUtils(orderid);
  return result;
}

// function used to add multiple offered variants to cart
// it also calls a function if passed after success full addition
export async function addOfferedVariantsToCart(
  variants,
  isSkipIntercept = false,
  isSellingPlanEnabled = false,
) {
  const qtyMap = variants.reduce((acc, curr) => {
    const { variant_id, properties } = curr;
    if (acc[variant_id]) {
      acc[variant_id].quantity += 1;
    } else {
      acc[variant_id] = {
        quantity: 1,
        properties,
      };
      if (isSellingPlanEnabled && curr.selling_plan)
        acc[variant_id].selling_plan = curr.selling_plan;
    }
    return acc;
  }, {});

  const items = Object.keys(qtyMap).map(key => {
    const { quantity, properties } = qtyMap[key];
    const formData = {};
    formData.quantity = quantity;
    formData.id = parseInt(key, 10);
    if (properties) {
      formData.properties = properties;
    }
    if (isSellingPlanEnabled && qtyMap[key].selling_plan)
      formData.selling_plan = qtyMap[key].selling_plan;

    return formData;
  });

  const data = { items };
  // as parallel modification of cart results in inconsistent data, adding mutex
  const shopifyCartMutex = mutexManager.getMutex(StaticMutexKeys.CART_MODIFY);
  try {
    await shopifyCartMutex.acquire();
    const resp = await fetch(
      isSkipIntercept ? `${ADD_TO_CART_API}&mrs=1` : ADD_TO_CART_API,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      },
    );
    logger.log('addOfferedVariantsToCart', resp);

    // failed to add the products
    if (resp.status !== 200) {
      const respData = await resp.json();
      const error = new Error('failed to add offered variants to cart');
      error.data = {
        extraErrorData: {
          ...data,
          respData,
        },
        functionName: 'addOfferedVariantsToCart',
        file: 'cart.js',
        level: 'error',
      };
      throw error;
    }
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: e.data,
      errorStack: e,
    });

    // failed to add the products
    return false;
  } finally {
    shopifyCartMutex.release();
  }

  // addition success
  refreshAJAXCart();
  return true;
}

const checkAutoAddLimit = async (
  toAdd,
  isSkipIntercept,
  autoAddLimit,
  productAddData,
  rulesEligibilityCount,
  isConsiderRepetetionOfRules,
  widgetID,
  isCalledFromCVB,
) => {
  // get the previous number of times variants were auto added
  let autoAddedProducts = {};

  const storedCVBMilestoneLimitData = getCVBAutoAddedVariantsLimits();
  if (isConsiderRepetetionOfRules) {
    const storedData = getAutoAddedVariantsDataWithRepetetionCount();
    const storedWidgetData = storedData && storedData[widgetID];

    if (storedWidgetData) {
      autoAddedProducts = storedData[widgetID].autoAddedProducts;
    }
  }

  if (!isConsiderRepetetionOfRules) {
    autoAddedProducts = getNumberOfTimesVariantWasAutoAdded();
  }

  // contains the number of time variant was or will be auto added
  let variantsToBeAutoAdded = {};
  let variantsToAdd = [];
  let autoAddLimitToUse = 0;
  let incrementAmount = 1;

  // the freeGiftAutoAddLimit value we get from the offer can be used if we aren't considering reservedItemQty or freeGiftAutoAddLimit value is not one
  if (
    (autoAddLimit !== 1 && isConsiderRepetetionOfRules) ||
    !isConsiderRepetetionOfRules
  ) {
    autoAddLimitToUse = autoAddLimit;
  }

  // if we are considering reservedItemQty, we need to modify we need to use rulesEligibilityCount as autoAddLimit
  if (autoAddLimit === 1 && isConsiderRepetetionOfRules) {
    autoAddLimitToUse = rulesEligibilityCount;
    incrementAmount = rulesEligibilityCount;
  }

  const updatedStoredMilestoneData = JSON.parse(
    JSON.stringify(storedCVBMilestoneLimitData),
  );

  // filters the variants if it's auto add limit is reached
  variantsToAdd = toAdd.filter(({ variant_id, milestoneMap }) => {
    let count = autoAddedProducts[variant_id] || 0;

    if (isCalledFromCVB) {
      Object.keys(milestoneMap).forEach(milestoneIdx => {
        const milestoneData = storedCVBMilestoneLimitData[milestoneIdx];
        if (milestoneData && milestoneData[variant_id]) {
          count = milestoneData[variant_id] || 0;
        }

        if (!milestoneData || !milestoneData[variant_id]) {
          count = 0;
        }
        if (!updatedStoredMilestoneData[milestoneIdx])
          updatedStoredMilestoneData[milestoneIdx] = {};
        updatedStoredMilestoneData[milestoneIdx][variant_id] = incrementAmount;
      });
    } else {
      // check if variant exist and update its number of time counter
      if (!variantsToBeAutoAdded[variant_id])
        variantsToBeAutoAdded[variant_id] = incrementAmount;
    }

    // check the variant limit is reached or not if yes we filter it
    return count < autoAddLimitToUse;
  });
  variantsToBeAutoAdded = { ...autoAddedProducts, ...variantsToBeAutoAdded };
  if (!variantsToAdd.length) {
    logRuntimeInfo({
      message: 'variant auto add limit reached',
      infoData: { variantsToBeAutoAdded, autoAddLimit },
      functionName: 'checkAutoAddLimit',
      fileName: 'cart',
    });
    return false;
  }

  const filteredProductAdd = [];

  // filtering the productAdd data since toAdd variable has been filtered above.
  productAddData.forEach(elem => {
    const filteredFromVariantsToAdd = variantsToAdd.filter(
      item => item.variant_id === elem.sp_variant_id,
    );
    if (filteredFromVariantsToAdd.length > 0) {
      filteredProductAdd.push({
        ...elem,
        quantity: filteredFromVariantsToAdd.length,
      });
    }
  });

  logger.log('variantsToAdd', variantsToAdd);

  const success = await addOfferedVariantsToCart(
    variantsToAdd,
    isSkipIntercept,
  );

  if (success) {
    // set the number of times variant was auto added in storage if addition was success
    setNumberOfTimesVariantWasAutoAdded(variantsToBeAutoAdded);
    if (isCalledFromCVB)
      setCVBAutoAddedVariantsLimits(updatedStoredMilestoneData);
    else
      setAutoAddedVariantsDataWithRepitionCount({
        widgetID,
        rulesEligibilityCount,
        autoAddedProducts: variantsToBeAutoAdded,
      });
    filteredProductAdd.forEach(elem => {
      const { sp_product_id, sp_variant_id, quantity, mr_offer_id } = elem;
      fgAddEvent({
        sp_product_id,
        sp_variant_id,
        quantity,
        mr_offer_id,
      });
    });
  }

  return success;
};

export function refreshAJAXCart() {
  refreshAJAXCartUtils();
}

export function debounce(func, timeout = 5000) {
  return debounceUtils(func, timeout);
}

export function getMRCartFromShopifyCart(cart) {
  return getMRCartFromShopifyCartUtils(cart);
}

export function getCartLevelDiscountAllocations(cart) {
  const { cart_level_discount_applications } = cart;
  return cart_level_discount_applications;
}

export function getCurrencyFromShopifyCart(cart) {
  return cart.currency;
}

export const getAmountOfVariantInCart = ({
  sp_variant_id,
  mrCart,
  isUseDiscountedPriceFromCart,
}) => {
  let amount = 0;
  for (let i = 0; i < mrCart.length; i += 1) {
    const { price, discounted_price, quantity, sp_variant_id: id } = mrCart[i];
    if (id === sp_variant_id) {
      amount +=
        (isUseDiscountedPriceFromCart ? discounted_price : price) * quantity;
    }
  }
  return amount;
};

export const getQtyOfVariantInCart = ({ sp_variant_id, mrCart }) => {
  let qty = 0;
  for (let i = 0; i < mrCart.length; i += 1) {
    const { quantity, sp_variant_id: id } = mrCart[i];
    if (id === sp_variant_id) {
      qty += quantity;
    }
  }
  return qty;
};

export async function cvbFreeGiftAdd(config) {
  const {
    mr_offer_id,
    isRefresh,
    isAutoRemove,
    isAutoAdd,
    allFreeGifts,
    freeGifts,
    mrCart,
    cartDiscountText,
    freeGiftAutoAddLimit,
    reservedItemQuantity,
    rulesEligibilityCount,
    isConsiderReservedItemQuantity = true,
    widgetID,
    // Below variable is used because we can't auto remove any product when an offer call is made from a product page.
    canBeAutoRemoved = true,
    isCalledFromCVB = false,
  } = config;

  const availedIDs = {};
  const updates = {};
  const toAdd = [];
  const productAddData = [];
  const productRemoveData = [];
  let isZeroPriceCheck = true;

  // this store specifically asked to remove 0 price check and allow customers
  // to take multiple quantities
  if (getShop() === 'niteowlnow.myshopify.com') {
    isZeroPriceCheck = false;
  }

  logger.log('freegifts', freeGifts);
  logger.log('cart', mrCart);

  for (let i = 0; i < freeGifts.length; i += 1) {
    const {
      freeGiftSpVariantID,
      freeGiftQuantity,
      freeGiftSpProductID,
      milestoneMap,
    } = freeGifts[i];
    let quantityNeeded = freeGiftQuantity;

    for (let j = 0; j < mrCart.length; j += 1) {
      const { sp_variant_id, quantity, key, price } = mrCart[j];

      // since reservedItemQuantity will tell us the no. of items needed to make the offer eligible, freegifts need to be added additionally
      if (
        isConsiderReservedItemQuantity &&
        reservedItemQuantity[j] &&
        sp_variant_id === freeGiftSpVariantID
      ) {
        quantityNeeded += reservedItemQuantity[j];
      }

      if (sp_variant_id === freeGiftSpVariantID) {
        if (quantity >= quantityNeeded) {
          const updatedQuantity = quantityNeeded;
          quantityNeeded = 0;

          // restrict quantity to available quantity when price is 0 or flag is set
          if (
            quantity !== updatedQuantity &&
            canBeAutoRemoved &&
            ((isZeroPriceCheck && price === 0) || isAutoRemove)
          ) {
            updates[key] = updatedQuantity;
          }
        } else {
          quantityNeeded -= quantity;
        }
      }
    }

    // if enought quantity of gift is not present then add it
    if (quantityNeeded > 0 && isAutoAdd) {
      // the addition function doesn't take quantity as variable
      // so add the freegift as many times as discount quantity
      for (let qty = 0; qty < quantityNeeded; qty += 1) {
        const variantsToAdd = {
          variant_id: freeGiftSpVariantID,
          properties: getMonkDiscountProperties({
            cartDiscountText,
          }),
          milestoneMap,
        };
        toAdd.push(variantsToAdd);
      }
      productAddData.push({
        sp_product_id: freeGiftSpProductID,
        sp_variant_id: freeGiftSpVariantID,
        quantity: quantityNeeded,
        mr_offer_id,
      });
    }

    // set availed id to use later when we have to remove non eligible products
    availedIDs[freeGiftSpVariantID] = true;
  }

  let autoAddedProducts = getNumberOfTimesVariantWasAutoAdded();
  if (isConsiderReservedItemQuantity) {
    const storedData = getAutoAddedVariantsDataWithRepetetionCount();
    if (storedData && storedData[widgetID]) {
      autoAddedProducts = storedData[widgetID].autoAddedProducts || {};
    } else {
      autoAddedProducts = {};
    }
  }
  const fgsLimitToBeReset = { ...autoAddedProducts };

  for (let i = 0; i < allFreeGifts.length; i += 1) {
    const { freeGiftSpVariantID } = allFreeGifts[i];

    // in case there a 0 free gifts then the offer is not eligible so
    // reset the quantities
    if (freeGifts.length === 0 && isAutoAdd) {
      fgsLimitToBeReset[freeGiftSpVariantID] = 0;
    }

    for (let j = 0; j < mrCart.length; j += 1) {
      // auto remove can happen only when we are either not considering reservedItemQty or  the case where we are considering reservedItemQty but when the item has not been reserved.
      const autoRemoveConditionForReservedItemQty =
        !isConsiderReservedItemQuantity ||
        (isConsiderReservedItemQuantity && !reservedItemQuantity[j]);

      const { sp_product_id, sp_variant_id, key, price, quantity } = mrCart[j];
      if (sp_variant_id === freeGiftSpVariantID) {
        if (!availedIDs[sp_variant_id]) {
          fgsLimitToBeReset[sp_variant_id] = 0;
          if (
            autoRemoveConditionForReservedItemQty &&
            canBeAutoRemoved &&
            (price === 0 || isAutoRemove)
          ) {
            updates[key] = 0;
            const removeQuantity = -quantity;
            productRemoveData.push({
              sp_product_id,
              sp_variant_id,
              quantity: removeQuantity,
            });
          }
        }
      }
    }
  }

  logger.log('toAdd', toAdd);
  // intercept this call to check CVB and discounted prices in case of no refresh
  const isSkipIntercept =
    !['revlon-co-in.myshopify.com'].includes(getShop()) || isRefresh;
  const isUpdateAvailable = Object.keys(updates).length > 0;
  let additionSuccess = true;
  let removalSuccess = true;
  removalSuccess = await updateCart({
    updates,
    isSkipIntercept,
  });

  if (removalSuccess && updates && Object.keys(updates).length > 0) {
    setNumberOfTimesVariantWasAutoAdded(fgsLimitToBeReset);
    setAutoAddedVariantsDataWithRepitionCount({
      widgetID,
      rulesEligibilityCount,
      autoAddedProducts: fgsLimitToBeReset,
    });
    const cvbModule = await import('../features/cvbModule');
    cvbModule.resetCVBFgVariantAutoAddLimit(fgsLimitToBeReset);
    productRemoveData.forEach(elem => {
      const { sp_product_id, sp_variant_id, quantity } = elem;
      fgAddEvent({
        sp_product_id,
        sp_variant_id,
        mr_offer_id,
        quantity,
      });
    });
    logRuntimeInfo({
      message: 'removed free gifts',
      infoData: {
        updates,
        freeGiftAutoAddLimit,
        rulesEligibilityCount,
        isConsiderReservedItemQuantity,
        mrCart,
      },
      functionName: 'cvbFreeGiftAdd',
      fileName: 'cart',
    });
  }

  if (additionSuccess && removalSuccess && toAdd.length > 0) {
    additionSuccess = await checkAutoAddLimit(
      toAdd,
      isSkipIntercept,
      freeGiftAutoAddLimit,
      productAddData,
      rulesEligibilityCount,
      isConsiderReservedItemQuantity,
      widgetID,
      isCalledFromCVB,
    );
  }
  if (!isUpdateAvailable) removalSuccess = false;
  const cartChanged = isUpdateAvailable || toAdd.length > 0;

  if (!additionSuccess && !removalSuccess) {
    return false;
  }

  if (cartChanged && isRefresh && !isCalledFromCVB) {
    goToCart({
      isPopup: false,
      isRefreshPageAfterProductAddition: isRefresh,
    });
  }

  return cartChanged;
}

// will run only if ship pro is either not enabled or not eligible.
export const removeShipProAndModifyCartIfNotEnabled = async ({
  mrCart,
  isRefresh,
}) => {
  const cartChanged = await removeShipProAndModifyCartIfNotEnabledUtils({
    mrCart,
    isRefresh,
  });
  return cartChanged;
};

// let embeddedModule;

export async function checkFreeGiftConditionsAndModifyCart({
  offer,
  mrCart,
  isEligible,
  canBeAutoRemoved,
}) {
  const {
    mr_offer_id,
    offered,
    primary,
    isAutoRemove,
    isAutoAdd = true,
    isFreegiftDiscountCodeEnabled,
    isRefresh,
    cartDiscountText,
    discountQuantity,
    offerLevelDiscountQuantity,
    isDiscountQuantityRestricted,
    isNumberOfOfferedProductsRestricted,
    freeGiftAutoAddLimit,
    isAutoAddDisabled,
    reservedItemQuantity,
    rulesEligibilityCount,
    widgetID,
  } = offer;

  if (!primary) {
    return false;
  }

  const { sp_variant_id: primaryVariantId } = primary;

  if (!mrCart) {
    const cart = await fetchCart();
    // as there were issues with the wrong variable being used, reassigning mrCart it is not set
    // eslint-disable-next-line no-param-reassign
    mrCart = getMRCartFromShopifyCart(cart);
  }

  let allFreeGifts = [];
  const autoFreeGifts = [];
  let manualFreeGifts = [];
  if (isAutoAdd) {
    offered.forEach(product => {
      const { variants, id: freeGiftSpProductID } = product;
      variants.forEach(variant => {
        const {
          id: freeGiftSpVariantID,
          quantityToAdd: freeGiftQuantity,
        } = variant;
        allFreeGifts.push({ freeGiftSpVariantID });
        autoFreeGifts.push({
          freeGiftSpProductID,
          freeGiftSpVariantID,
          freeGiftQuantity,
        });
      });
    });
  } else {
    const {
      allFreeGifts: manualAll,
      freegifts: manualFGs,
    } = getManualFGsWithDiscountQuantity({
      mrCart,
      offered,
      discountQuantity,
      offerLevelDiscountQuantity,
      isDiscountQuantityRestricted,
      isNumberOfOfferedProductsRestricted,
      primaryVariantId,
      reservedItemQuantity,
      rulesEligibilityCount,
    });

    allFreeGifts = manualAll;
    manualFreeGifts = manualFGs;
  }

  let freeGifts = [];

  let freegiftDiscountCodeEligible = true;
  if (isFreegiftDiscountCodeEnabled) {
    const commonModuleForEmbeddedAndAutoAdd = await import('../features/commonModuleForEmbeddedAndAutoAdd');
    const appliedCode = getMrDiscountCode();
    if (
      !commonModuleForEmbeddedAndAutoAdd.isFreegiftCodeValid(
        appliedCode,
        offer.freegiftDiscountCodes,
      )
    ) {
      freegiftDiscountCodeEligible = false;
    }
  }

  if (isEligible && freegiftDiscountCodeEligible) {
    freeGifts = isAutoAdd ? autoFreeGifts : manualFreeGifts;
  }

  const config = {
    mr_offer_id,
    isRefresh,
    isAutoRemove,
    isAutoAdd: isAutoAdd && !isAutoAddDisabled,
    allFreeGifts,
    freeGifts,
    mrCart,
    cartDiscountText,
    primaryVariantId,
    freeGiftAutoAddLimit,
    reservedItemQuantity,
    rulesEligibilityCount,
    widgetID,
    isConsiderReservedItemQuantity: isEligible,
    canBeAutoRemoved,
  };

  const changed = await cvbFreeGiftAdd(config);
  return changed;
}

function getManualFGsWithDiscountQuantity({
  mrCart,
  offered,
  discountQuantity,
  offerLevelDiscountQuantity,
  isDiscountQuantityRestricted,
  isNumberOfOfferedProductsRestricted,
  reservedItemQuantity,
  rulesEligibilityCount,
}) {
  // map to track all the freegifts with quantities
  const freegiftMap = {};
  const allFreeGifts = [];
  const setFreeGiftMap = (id, count) => {
    if (freegiftMap[id]) {
      freegiftMap[id] += count;
      return;
    }
    freegiftMap[id] = count;
  };

  // track how many quantities are remaining on the offer level so that it can be used to apply discount
  let offerLevelDiscountQuantityRemaining =
    offerLevelDiscountQuantity * rulesEligibilityCount;
  for (let i = 0; i < offered.length; i += 1) {
    // track how many quantities are remaining on the product level so that it can be used to apply discount
    let discountQuantityRemaining = discountQuantity * rulesEligibilityCount;

    const { variants } = offered[i];
    for (let j = 0; j < variants.length; j += 1) {
      const { id } = variants[j];
      allFreeGifts.push({ freeGiftSpVariantID: id });
      for (let k = 0; k < mrCart.length; k += 1) {
        const { sp_variant_id, quantity } = mrCart[k];
        if (sp_variant_id === id) {
          // check if offer level discount quantity has exhausted
          if (
            isNumberOfOfferedProductsRestricted &&
            offerLevelDiscountQuantity <= 0
          ) {
            // eslint-disable-next-line no-continue
            continue;
          }

          // check if product level discount quantity has exhausted
          if (isDiscountQuantityRestricted && discountQuantityRemaining <= 0) {
            // eslint-disable-next-line no-continue
            continue;
          }

          // updated quantity contains the how many gifts should be allowed
          let updatedQuantity = quantity;

          // if item exists in reservedItemQuantity, then we need to subtract reserved quantity from allowed gifts
          if (
            reservedItemQuantity &&
            reservedItemQuantity[k] &&
            quantity >= reservedItemQuantity[k]
          ) {
            updatedQuantity -= reservedItemQuantity[k];
          }

          if (isDiscountQuantityRestricted) {
            updatedQuantity = Math.min(
              updatedQuantity,
              discountQuantityRemaining,
            );
          }

          if (isNumberOfOfferedProductsRestricted) {
            updatedQuantity = Math.min(
              updatedQuantity,
              offerLevelDiscountQuantityRemaining,
            );
          }

          // if any quantity is left for discount then add it to the free gift map
          // and reduce the remaining quantities
          if (updatedQuantity > 0) {
            setFreeGiftMap(id, updatedQuantity);
            discountQuantityRemaining -= updatedQuantity;
            offerLevelDiscountQuantityRemaining -= updatedQuantity;
          }
        }
      }
    }
  }

  // convert the map of freegifts into array
  const freegifts =
    Object.keys(freegiftMap).map(freeGiftSpVariantID => ({
      freeGiftSpVariantID: parseInt(freeGiftSpVariantID, 10),
      freeGiftQuantity: freegiftMap[freeGiftSpVariantID],
    })) || [];
  return { freegifts, allFreeGifts };
}

export function getMonkDiscountProperties({
  isDiscountDisabled,
  off,
  offerType,
  properties = {},
  cartDiscountText,
}) {
  // eslint-disable-next-line no-underscore-dangle
  properties._mr = '1';
  if (
    [
      'biosourcenutra.myshopify.com',
      'zabedo.myshopify.com',
      'deichbrise-onlineshop.myshopify.com',
      'lilacst.myshopify.com',
      'sbmlimited.myshopify.com',
    ].includes(getShop())
  ) {
    properties = {};
  }

  // if no discount return
  if (isDiscountDisabled) {
    return properties;
  }

  // add key value for properties
  let discountText = '';
  let discountKey = '';

  const parts = cartDiscountText.split(' ');
  if (parts.length > 1) {
    discountKey = parts[0];
    discountText = parts.slice(1).join(' ');
  }

  // TODO: add translation support for this
  if (getShop() === 'indy-com-ar.myshopify.com') {
    discountKey = 'Descuento';
    discountText =
      offerType === 'bundle'
        ? 'Se aplica en el pago'
        : `${off} en este producto (Se aplica en el pago)`;
  }

  if (
    ['liebeselement.myshopify.com', 'meineravendeals.myshopify.com'].includes(
      getShop(),
    )
  ) {
    discountKey = 'Rabatt';
    discountText =
      offerType === 'bundle'
        ? discountText
        : `${off} wird an der Kasse angewandt`;
  }

  if (getShop() === 'office-for-proficient-enterprise.myshopify.com') {
    discountKey = 'Rabatt';
    discountText = `${off}`;
  }

  if (getShop() === 'wheelhousecycleclub.myshopify.com') {
    discountKey = 'Discount';
    discountText = 'Will be applied at checkout';
  }

  if (getShop() === 'deitydesigns.myshopify.com') {
    discountKey = 'Discount';
    discountText = 'applied on checkout';
  }

  if (getShop() === 'luxurytowels.myshopify.com') {
    discountText = 'Applicable on checkout';
  }

  if (getShop() === 'opeplan-elpaso.myshopify.com') {
    discountKey = `${off}`;
    discountText = 'wird während des Bezahlvorgangs angewendet';
  }

  if (getShop() === 'lrl-motors.myshopify.com') {
    discountKey = 'Free';
    discountText = 'Gift';
  }

  if (discountKey !== '' && discountText !== '') {
    properties[discountKey] = discountText;
  }
  return properties;
}

export function increaseQuantityAndGetMrCart(cart, variants) {
  let mrCart = getMRCartFromShopifyCart(cart);
  for (let j = 0; j < variants.length; j++) {
    mrCart = increaseMrCartQuantity({ variant: variants[j], mrCart });
  }
  return mrCart;
}

export function increaseMrCartQuantity({ variant, mrCart }) {
  let found = false;
  for (let i = 0; i < mrCart.length; i++) {
    if (
      mrCart[i].sp_product_id == variant.product_id &&
      mrCart[i].sp_variant_id == variant.variant_id
    ) {
      mrCart[i].quantity += 1;
      found = true;
      break;
    }
  }

  if (!found) {
    mrCart.push({
      sp_product_id: variant.product_id,
      sp_variant_id: variant.variant_id,
      quantity: 1,
    });
  }
  return mrCart;
}

function getVariantLineFromCartWithoutProperties(variantID, cart) {
  for (let i = 0; i < cart.items.length; i++) {
    if (cart.items[i].variant_id === Number(variantID)) {
      return { quantity: cart.items[i].quantity, line: i + 1 };
    }
  }
  return false;
}

export async function changeCartVariants(variantsWithQuantity) {
  const cart = await fetchCart();

  const updates = {};
  for (let i = 0; i < variantsWithQuantity.length; i += 1) {
    let remaining = variantsWithQuantity[i].quantity;
    const { properties, sp_variant_id } = variantsWithQuantity[i];
    for (let j = 0; j < cart.items.length; j += 1) {
      if (
        cart.items[j].variant_id === sp_variant_id &&
        arePropertiesEqual(properties, cart.items[j].properties)
      ) {
        updates[cart.items[j].key] = remaining;
        remaining = 0;
      }
    }
  }
  const httpArgs = [`${UPDATE_CART_API}&mrs=1`, { updates }];
  await axios.post(...httpArgs);
}

export function getVariantQuantityAndLineFromCart(variantID, properties, cart) {
  variantID = Number(variantID);

  // as the products with different customizations are different line items include them in the count
  let quantity = 0;
  let idx = -1;
  for (let i = 0; i < cart.items.length; i++) {
    if (
      cart.items[i].variant_id === variantID &&
      arePropertiesEqual(properties, cart.items[i].properties)
    ) {
      quantity += cart.items[i].quantity;
      idx = i;
      break;
    }
  }
  return { quantity, line: idx + 1 };
}
function arePropertiesEqual(source, target) {
  try {
    let sourceKeys = [];
    let targetKeys = [];
    if (!source) {
      source = {};
    }
    if (!target) {
      target = {};
    }
    sourceKeys = Object.keys(source);
    targetKeys = Object.keys(target);
    if (sourceKeys.length !== targetKeys.length) {
      return false;
    }

    for (const key of sourceKeys) {
      if (source[key] !== target[key]) {
        return false;
      }
    }
    return true;
  } catch (e) {
    logger.log('arePropertiesEqual', e);
    return false;
  }
}

export function cartHasSellingPlanItem(cart) {
  if (getShop() === 'orderflow-labs.myshopify.com') {
    return false;
  }
  if (getShop() === 'byron-moonshine-coffee.myshopify.com') {
    return false;
  }
  if (isInaEss()) {
    return false;
  }

  const { items } = cart;
  if (items) {
    for (let i = 0; i < items.length; i++) {
      // check if allocation is there
      const { selling_plan_allocation } = items[i];
      if (selling_plan_allocation) {
        // check if plan is there
        const { selling_plan } = selling_plan_allocation;
        if (selling_plan) {
          // check if id is valid
          const { id } = selling_plan;
          if (id && parseInt(id) > 0) {
            return true;
          }
        }
      }

      const { properties } = items[i];
      if (properties) {
        const { group_id } = properties;
        if (group_id && parseInt(group_id) > 0) {
          return true;
        }
      }
    }
  }
  return false;
}

// function used to add multiple variants to cart
export async function addVariantsWithQuantityToCart({
  variantsWithQuantity,
  isSkipIntercept = false,
}) {
  const items = [];
  variantsWithQuantity.map(variant => {
    const formData = {};
    formData.quantity = variant.quantity;
    formData.id = parseInt(variant.sp_variant_id);
    if (variant.properties) {
      formData.properties = variant.properties;
    }
    items.push(formData);
  });
  // as parallel modification of cart results in inconsistent data, adding mutex
  const shopifyCartMutex = mutexManager.getMutex(StaticMutexKeys.CART_MODIFY);
  try {
    await shopifyCartMutex.acquire();

    const url = isSkipIntercept ? `${ADD_TO_CART_API}&mrs=1` : ADD_TO_CART_API;
    const httpArgs = [url, { items }];
    const response = await axios.post(...httpArgs);
    return response;
  } finally {
    shopifyCartMutex.release();
  }
}

export async function addToCart(
  variantid,
  properties,
  isSkipIntercept = false,
) {
  // as parallel modification of cart results in inconsistent data, adding mutex
  const shopifyCartMutex = mutexManager.getMutex(StaticMutexKeys.CART_MODIFY);
  try {
    await shopifyCartMutex.acquire();
    const formData = { quantity: 1, id: parseInt(variantid, 10), properties };
    const apiURL = isSkipIntercept
      ? `${ADD_TO_CART_API}&mrs=1`
      : ADD_TO_CART_API;
    const httpArgs = [apiURL, formData];
    const response = await axios.post(...httpArgs);
    return response;
  } finally {
    shopifyCartMutex.release();
  }
}

// TODO: remove duplicate functions by making keys unniform, one function has sp_variant_id
// and the other has variant_id
export function increaseQuantityAndGetMrCartForVariantsWithQuantity(
  cart,
  variantsWithQuantity,
) {
  let mrCart = getMRCartFromShopifyCart(cart);
  for (let j = 0; j < variantsWithQuantity.length; j++) {
    mrCart = increaseMrCartQuantityForSpVariant({
      variant: variantsWithQuantity[j],
      mrCart,
    });
  }
  return mrCart;
}

export function increaseMrCartQuantityForSpVariant({ variant, mrCart }) {
  let found = false;
  for (let i = 0; i < mrCart.length; i++) {
    if (
      mrCart[i].sp_product_id == variant.sp_product_id &&
      mrCart[i].sp_variant_id == variant.sp_variant_id
    ) {
      mrCart[i].quantity += variant.quantity;
      found = true;
      break;
    }
  }

  if (!found) {
    mrCart.push({
      sp_product_id: variant.sp_product_id ? variant.sp_product_id : 0,
      sp_variant_id: variant.sp_variant_id,
      quantity: variant.quantity,
    });
  }
  return mrCart;
}

export function getVariantsQuantityFromCart(variantsWithQuantity, cart) {
  const out = variantsWithQuantity.reduce((items, item) => {
    const lineItem = getVariantQuantityAndLineFromCart(
      item.sp_variant_id,
      item.properties,
      cart,
    );
    const { quantity, line } = lineItem;
    return items.concat({
      sp_variant_id: item.sp_variant_id,
      quantity,
      line,
      properties: item.properties,
    });
  }, []);
  return out;
}

export function updateProductIDsInCartForPrimary({
  cart,
  variantsWithQuantity,
}) {
  for (let i = 0; i < variantsWithQuantity.length; i++) {
    cart = updateProductIdInCartForPrimary({
      cart,
      primary: variantsWithQuantity[i],
    });
  }
  return cart;
}

export function updateProductIdInCartForPrimary({ cart, primary }) {
  for (let i = 0; i < cart.length; i++) {
    if (
      cart[i].sp_product_id === 0 &&
      cart[i].sp_variant_id === primary.sp_variant_id
    ) {
      cart[i].sp_product_id = primary.sp_product_id;
    }
  }
  return cart;
}

export function goToCartPermalink({ cart: mrCart, orderid }) {
  const items = mrCart.reduce(
    (curr, item) => curr.concat(`${item.sp_variant_id}:${item.quantity}`),
    [],
  );

  const itemPath = items.join(',');
  const url = `/cart/${itemPath}?attributes[_mrOrderID]=${orderid}`;
  window.location = url;
}

export async function getVDVariantsToAdd({
  productId,
  selectedVariants, // below atc
  variantsWithQuantity, // above atc
  minQty,
  isDiscountDisabled,
  discountQuantity,
  isDiscountQuantityRestricted,
  theme,
  offerType,
  isSkipIfAlreadyInCart = true,
}) {
  let quantityMap = {};
  if (isSkipIfAlreadyInCart) {
    const latestCart = await fetchCart();
    const mrCart = getMRCartFromShopifyCart(latestCart);
    quantityMap = getCartQuantityMap({ mrCart });
  }

  let variantsIdentifiers = [];
  if (Array.isArray(variantsWithQuantity)) {
    variantsIdentifiers = variantsWithQuantity.map(x => ({
      spVariantID: x.sp_variant_id,
      quantity: x.quantity,
      sellingPlan: x.selling_plan,
    }));
  }
  if (Array.isArray(selectedVariants)) {
    variantsIdentifiers = selectedVariants.map(x => ({
      spVariantID: x.spVariantID,
      sellingPlan: x.selling_plan,
    }));
  }

  let variants = [];

  variantsIdentifiers.forEach(selectedVariant => {
    const { spVariantID, sellingPlan } = selectedVariant;
    let { quantity } = selectedVariant;

    if (quantity === undefined) {
      quantity = selectedVariants.reduce(
        (count, variant) =>
          variant.spVariantID === spVariantID ? count + 1 : count,
        0,
      );
    }

    const differenceQuantity = quantity - (quantityMap[spVariantID] || 0);

    if (differenceQuantity > 0) {
      for (let i = 0; i < differenceQuantity; i += 1) {
        const variant = {
          product_id: productId,
          variant_id: spVariantID,
          properties: getMonkDiscountProperties({
            isDiscountDisabled,
            off: '',
            offerType,
            properties: {},
            discountQuantity,
            isDiscountQuantityRestricted,
            cartDiscountText: theme.cartDiscountText,
          }),
        };
        if (sellingPlan) variant.selling_plan = +sellingPlan;
        variants.push(variant);
      }

      quantityMap[spVariantID] = quantity;
    }
  });

  if (
    typeof minQty === 'number' &&
    variants.length < minQty &&
    variants.length > 0 // if there is no variants, it means variants are already present in the cart & do not add minQty
  ) {
    variants = [
      ...variants,
      ...Array(minQty - variants.length).fill(variants[0]),
    ];
  }

  return variants;
}

// Not changing the function name but this is used for bundle, FBT and Product Addons
export async function getBundleOrAddonOrFBTVariantsToAdd({
  products,
  selectedVariantInfo,
  isDiscountDisabled,
  discountQuantity,
  isDiscountQuantityRestricted,
  theme,
  offerType,
  isSkipIfAlreadyInCart = true,
  isSellingPlanEnabled = false,
}) {
  const latestCart = await fetchCart();
  const cart = getMRCartFromShopifyCart(latestCart);

  return products.reduce((acc, product, idx) => {
    // add only those products which are not present in cart or if skip is false
    if (
      !isOfferedProductPresentInCartArgs({ idx, products, cart }) ||
      !isSkipIfAlreadyInCart
    ) {
      const productData = {
        variant_id: selectedVariantInfo[idx].id,
        product_id: product.id,
        properties: getMonkDiscountProperties({
          isDiscountDisabled,
          off: '',
          offerType,
          properties: product.properties || {},
          discountQuantity,
          isDiscountQuantityRestricted,
          cartDiscountText: theme.cartDiscountText,
        }),
      };
      if (isSellingPlanEnabled && product.selling_plan)
        productData.selling_plan = product.selling_plan;

      return acc.concat(productData);
    }
    return acc;
  }, []);
}

function isOfferedProductPresentInCartArgs({ idx, cart, products }) {
  const offeredProductPresentMap = getOfferProductsInCart({
    products,
    cart,
  });
  return offeredProductPresentMap[idx];
}

const getCartQuantityMap = ({ mrCart }) =>
  mrCart.reduce((curr, item) => {
    const { sp_variant_id, quantity } = item;
    const key = sp_variant_id.toString();
    if (curr[key]) {
      curr[key] += quantity;
    } else {
      curr[key] = quantity;
    }
    return curr;
  }, {});

function getOfferProductsInCart({ products, cart }) {
  const quantityMap = getCartQuantityMap({ mrCart: cart });

  const productPresentMap = {};
  for (let i = 0; i < products.length; i++) {
    const { variants } = products[i];
    for (let j = 0; j < variants.length; j++) {
      const { id } = variants[j];
      const key = id.toString();
      if (quantityMap[key] > 0) {
        productPresentMap[i] = true;
        quantityMap[key]--;
        break;
      }
    }
  }
  return productPresentMap;
}

export function isPresentInCart(product_id, variant_id, cart) {
  return cart.items.some(ele => {
    if (parseInt(variant_id) === 0) {
      return ele.product_id === parseInt(product_id);
    }
    return (
      ele.product_id === parseInt(product_id) && ele.id === parseInt(variant_id)
    );
  });
}

export function _isVariantPresentInCart({ sp_variant_id, cart }) {
  return cart.some(ele => ele.sp_variant_id === sp_variant_id);
}

function isPrimaryVariantASubscriptionProduct({ primaryVariantId, mrCart }) {
  let isSubscriptionPresent = false;
  for (let i = 0; i < mrCart.length; i += 1) {
    const item = mrCart[i];
    const { selling_plan_allocation, sp_variant_id } = item;
    if (sp_variant_id === primaryVariantId && selling_plan_allocation) {
      const { selling_plan } = selling_plan_allocation;
      if (selling_plan) {
        const { id } = selling_plan;
        if (id > 0) {
          isSubscriptionPresent = true;
          break;
        }
      }
    }
  }
  return isSubscriptionPresent;
}

export function isAppliedCodeValid({ appliedCode, freegiftDiscountCodes }) {
  for (let i = 0; i < freegiftDiscountCodes.length; i += 1) {
    if (
      matchStringsWithoutCaseSensitivity(appliedCode, freegiftDiscountCodes[i])
    ) {
      return true;
    }
  }
  return false;
}
